import { Modal, TextInput } from '@medeloop/medeloop-components-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UpdateUserDTO } from 'src/api/users';
import {
  useUpdateUserMutation,
  useVerifyUserAttribute,
} from 'src/api/users-queries';
import { MfaCodeInput } from '../mfa-code-input/mfa-code-input';

interface UpdateEmailModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  userProfile: UpdateUserDTO;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const UpdateEmailModal = ({
  isModalOpen,
  setIsModalOpen,
  userProfile,
}: UpdateEmailModalProps) => {
  const { t } = useTranslation();

  const [emailAddress, setEmailAddress] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [verficationCodeSent, setVerificationCodeSent] = useState(false);
  const [code, setCode] = useState(['', '', '', '', '', '']);

  const updateUserMutation = useUpdateUserMutation({ showConfirmationToast: false });
  const verifyUserAttributeMutation = useVerifyUserAttribute('email');

  const closeAndResetChangeEmailModal = () => {
    setIsModalOpen(false);
    setVerificationCodeSent(false);
    setIsEmailValid(true);
    setEmailAddress('');
    setCode(['', '', '', '', '', '']);
  };

  const handleUpdateEmail = async () => {
    if (verficationCodeSent) {
      const verificationCode = code.join('');
      await verifyUserAttributeMutation.mutate({ code: verificationCode });
    } else {
      await updateUserMutation.mutate({
        ...userProfile,
        email: emailAddress,
      });
      setVerificationCodeSent(true);
    }
  };

  const handleEmailChange = (email: string) => {
    if (EMAIL_REGEX.test(email)) {
      setIsEmailValid(true);
    } else {
      setIsEmailValid(false);
    }
    setEmailAddress(email);
  };

  useEffect(() => {
    if (verifyUserAttributeMutation.isSuccess) {
      closeAndResetChangeEmailModal();
    }
  }, [verifyUserAttributeMutation.isSuccess]);

  return (
    <Modal
      title={verficationCodeSent ? 'Enter the verification code' : 'Change email'}
      isOpen={isModalOpen}
      actionType="primary"
      onCancel={() => closeAndResetChangeEmailModal()}
      actionText={verficationCodeSent ? 'Change email' : 'Next'}
      cancelText={t('cancel')}
      actionFunction={handleUpdateEmail}
      disabled={!isEmailValid}
    >
      <div className="flex flex-col gap-4 w-[432px] text-sm text-content-on-dark-default">
        {verficationCodeSent ? (
          <>
            <span>
              We’ve sent a 6-digit verification code to your email. Please enter it below to confirm
              your new email address.
            </span>
            <MfaCodeInput code={code} setCode={setCode} evaluateMFA={handleUpdateEmail} />
            {verifyUserAttributeMutation.isError && (
              <span className="text-red-500 text-sm">The code inserted was incorrect</span>
            )}
          </>
        ) : (
          <>
            <span>
              We’ll send a verification code to your new email address. This change will apply
              across all Medeloop platforms you’re part of.
            </span>
            <TextInput
              label="Enter the new email address you’d like to use "
              placeHolder="Enter your new email address"
              value={emailAddress}
              onChange={(e) => handleEmailChange(e.target.value)}
              isError={!isEmailValid}
              autoFocus
            />
          </>
        )}
      </div>
    </Modal>
  );
};
